import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Grid, Card, CardContent, CardHeader, Typography, CircularProgress, Alert, Button, TextField, Divider, Switch,
  FormControlLabel, Snackbar, Tooltip, IconButton,
} from '@mui/material';
import { Save as SaveIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { formatDateTime } from '../../utils/dateUtils';
import { AdminPageHeader, errorMessage } from '../../components/Admin/adminUi';

const APP_FIELDS = [
  { key: 'name', label: 'Application name', type: 'text' },
  { key: 'supportEmail', label: 'Support email', type: 'email' },
];
const POLICY_NUMBERS = [
  { key: 'minLength', label: 'Minimum length', min: 4, max: 128 },
  { key: 'maxAge', label: 'Max password age (days, 0 = never)', min: 0, max: 3650 },
  { key: 'preventReuse', label: 'Prevent reuse of last N passwords', min: 0, max: 50 },
];
const POLICY_FLAGS = [
  { key: 'requireUppercase', label: 'Require uppercase letters' },
  { key: 'requireLowercase', label: 'Require lowercase letters' },
  { key: 'requireNumbers', label: 'Require numbers' },
  { key: 'requireSpecialChars', label: 'Require special characters' },
];
const AI_NUMBERS = [
  { key: 'requestsPerMinute', label: 'Requests per minute', min: 1, max: 1000 },
  { key: 'requestsPerDay', label: 'Requests per day', min: 1, max: 100000 },
  { key: 'concurrency', label: 'Concurrent requests', min: 1, max: 50 },
];

const extract = (settings) => ({
  application: {
    name: settings?.application?.name ?? '',
    supportEmail: settings?.application?.supportEmail ?? '',
    maintenanceMode: Boolean(settings?.application?.maintenanceMode),
  },
  passwordPolicy: {
    minLength: settings?.security?.passwordPolicy?.minLength ?? 8,
    maxAge: settings?.security?.passwordPolicy?.maxAge ?? 90,
    preventReuse: settings?.security?.passwordPolicy?.preventReuse ?? 5,
    requireUppercase: Boolean(settings?.security?.passwordPolicy?.requireUppercase),
    requireLowercase: Boolean(settings?.security?.passwordPolicy?.requireLowercase),
    requireNumbers: Boolean(settings?.security?.passwordPolicy?.requireNumbers),
    requireSpecialChars: Boolean(settings?.security?.passwordPolicy?.requireSpecialChars),
  },
  ai: {
    requestsPerMinute: settings?.ai?.requestsPerMinute ?? 15,
    requestsPerDay: settings?.ai?.requestsPerDay ?? 1500,
    concurrency: settings?.ai?.concurrency ?? 2,
    backgroundShare: settings?.ai?.backgroundShare ?? 0.7,
  },
});

/** Returns only the keys of `next` that differ from `orig`. */
const diff = (orig, next) => Object.keys(next).reduce((acc, k) => (String(next[k]) !== String(orig[k]) ? { ...acc, [k]: next[k] } : acc), {});

const AdminSystemSettings = () => {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState(extract(null));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.admin.getSystemSettings();
      setSettings(res.data);
      setForm(extract(res.data));
    } catch (err) {
      setError(errorMessage(err, 'Failed to load system settings'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const original = useMemo(() => extract(settings), [settings]);

  // Server deep-merges, so we only send the subtrees that changed.
  const payload = useMemo(() => {
    const out = {};
    const app = diff(original.application, form.application);
    if (Object.keys(app).length) out.application = app;
    const policy = diff(original.passwordPolicy, {
      ...form.passwordPolicy,
      ...POLICY_NUMBERS.reduce((acc, { key }) => ({ ...acc, [key]: Number(form.passwordPolicy[key]) }), {}),
    });
    if (Object.keys(policy).length) out.security = { passwordPolicy: policy };
    const ai = diff(original.ai, {
      ...form.ai,
      ...AI_NUMBERS.reduce((acc, { key }) => ({ ...acc, [key]: Number(form.ai[key]) }), {}),
      backgroundShare: Number(form.ai.backgroundShare),
    });
    if (Object.keys(ai).length) out.ai = ai;
    return out;
  }, [original, form]);

  const dirty = Object.keys(payload).length > 0;

  const save = async () => {
    if (!dirty) return;
    try {
      setSaving(true);
      setError(null);
      const res = await apiService.admin.updateSystemSettings(payload);
      const next = res.data.settings || res.data;
      setSettings(next);
      setForm(extract(next));
      setToast('System settings saved');
    } catch (err) {
      setError(errorMessage(err, 'Failed to save system settings'));
    } finally {
      setSaving(false);
    }
  };

  const setApp = (key, value) => setForm((f) => ({ ...f, application: { ...f.application, [key]: value } }));
  const setPolicy = (key, value) => setForm((f) => ({ ...f, passwordPolicy: { ...f.passwordPolicy, [key]: value } }));
  const setAi = (key, value) => setForm((f) => ({ ...f, ai: { ...f.ai, [key]: value } }));

  if (loading && !settings) {
    return <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px"><CircularProgress /></Box>;
  }

  return (
    <Box>
      <AdminPageHeader
        title="System settings"
        subtitle="Platform-wide defaults that apply to every tenant"
        actions={(
          <>
            <Tooltip title="Reload"><IconButton onClick={load} disabled={saving}><RefreshIcon /></IconButton></Tooltip>
            <Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />} disabled={!dirty || saving} onClick={save}>
              Save changes
            </Button>
          </>
        )}
      />
      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}
      {form.application.maintenanceMode && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Maintenance mode is {original.application.maintenanceMode ? 'ON' : 'about to be turned ON'}. Customers may be blocked from using the application.
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardHeader title="Application" titleTypographyProps={{ variant: 'h6' }} />
            <Divider />
            <CardContent>
              {APP_FIELDS.map((f) => (
                <TextField key={f.key} fullWidth size="small" margin="normal" type={f.type} label={f.label}
                  value={form.application[f.key]} onChange={(e) => setApp(f.key, e.target.value)} />
              ))}
              <FormControlLabel sx={{ mt: 1 }} label="Maintenance mode"
                control={<Switch color="warning" checked={form.application.maintenanceMode} onChange={(e) => setApp('maintenanceMode', e.target.checked)} />} />
              <Typography variant="caption" color="text.secondary" display="block">
                Version {settings?.application?.version || 'n/a'}
                {settings?.lastUpdated ? ` · last updated ${formatDateTime(settings.lastUpdated)}${settings.updatedBy ? ` by ${settings.updatedBy}` : ''}` : ''}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardHeader title="Default password policy" titleTypographyProps={{ variant: 'h6' }} subheader="Applies to tenants without their own policy" />
            <Divider />
            <CardContent>
              <Grid container spacing={2}>
                {POLICY_NUMBERS.map((f) => (
                  <Grid item xs={12} sm={4} key={f.key}>
                    <TextField fullWidth size="small" type="number" label={f.label} inputProps={{ min: f.min, max: f.max }}
                      value={form.passwordPolicy[f.key]} onChange={(e) => setPolicy(f.key, e.target.value)} />
                  </Grid>
                ))}
              </Grid>
              <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column' }}>
                {POLICY_FLAGS.map((f) => (
                  <FormControlLabel key={f.key} label={f.label}
                    control={<Switch checked={form.passwordPolicy[f.key]} onChange={(e) => setPolicy(f.key, e.target.checked)} />} />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardHeader title="AI rate limits" titleTypographyProps={{ variant: 'h6' }} subheader="Global budget for Google AI requests across all tenants" />
            <Divider />
            <CardContent>
              <Grid container spacing={2}>
                {AI_NUMBERS.map((f) => (
                  <Grid item xs={12} sm={4} key={f.key}>
                    <TextField fullWidth size="small" type="number" label={f.label} inputProps={{ min: f.min, max: f.max }}
                      value={form.ai[f.key]} onChange={(e) => setAi(f.key, e.target.value)} />
                  </Grid>
                ))}
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Background share"
                    inputProps={{ min: 0.1, max: 1, step: 0.05 }}
                    value={form.ai.backgroundShare}
                    onChange={(e) => setAi('backgroundShare', e.target.value)}
                    helperText="Portion of each minute's budget that background jobs may use; the rest is reserved for interactive requests"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast('')} message={toast} />
    </Box>
  );
};

export default AdminSystemSettings;
